import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConstellationMapScreen from './ConstellationMapScreen';
import useZodiacCrushMapProgress from '../../hooks/useZodiacCrushMapProgress';
import { authenticatedRequest } from '../../../services/authApi';
import { isZodiacCrushQaUnlockEnabled } from '../utils/qaUnlock';
import { REST_API_ORIGIN } from '../../../platform/endpoints';
import { ZodiacCrushMapSummary } from './ZodiacCrushMapLocalizationSurface';
import { getAuthToken } from '../../../utils/auth';

// --- Small auth helper (matches the pattern used elsewhere in the game UI) ---
const ZC_API_BASE = REST_API_ORIGIN;
const readAuthToken = () => {
  try {
    return getAuthToken();
  } catch (_) {
    return '';
  }
};

async function zcMapGet(path) {
  const token = readAuthToken();
  const url = `${ZC_API_BASE}${path}`;

  const resp = await authenticatedRequest(url, {
    rawResponse: true,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    diagSource: 'ZodiacCrushMapScreen',
  });

  let data = null;
  try {
    data = await resp.json();
  } catch (_) {
    data = null;
  }

  if (!resp.ok || !data?.ok) {
    const msg = data?.message || data?.error || `Request failed (${resp.status})`;
    throw new Error(msg);
  }

  return data;
}

const WORLD_LABELS_ES = [
  { key: 'aries', label: 'Aries', glyph: '♈' },
  { key: 'taurus', label: 'Tauro', glyph: '♉' },
  { key: 'gemini', label: 'Géminis', glyph: '♊' },
  { key: 'cancer', label: 'Cáncer', glyph: '♋' },
  { key: 'leo', label: 'Leo', glyph: '♌' },
  { key: 'virgo', label: 'Virgo', glyph: '♍' },
  { key: 'libra', label: 'Libra', glyph: '♎' },
  { key: 'scorpio', label: 'Escorpio', glyph: '♏' },
  { key: 'sagittarius', label: 'Sagitario', glyph: '♐' },
  { key: 'capricorn', label: 'Capricornio', glyph: '♑' },
  { key: 'aquarius', label: 'Acuario', glyph: '♒' },
  { key: 'pisces', label: 'Piscis', glyph: '♓' },
];

const SIGN_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',

  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',

  gemini: 'air',
  libra: 'air',
  aquarius: 'air',

  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
};

// Colores “candy” por elemento (según lo que pediste)
const ELEMENT_THEME = {
  fire: {
    tint: 'rgba(255, 59, 59, 0.22)',        // rojo fuego
    tintStrong: 'rgba(255, 59, 59, 0.34)',
    border: 'rgba(255, 59, 59, 0.26)',
    barA: 'rgba(255, 59, 59, 0.92)',
    barB: 'rgba(255, 59, 59, 0.62)',
    emoji: '🔥',
  },
  air: {
    tint: 'rgba(34, 197, 94, 0.20)',        // verde aire
    tintStrong: 'rgba(34, 197, 94, 0.32)',
    border: 'rgba(34, 197, 94, 0.24)',
    barA: 'rgba(34, 197, 94, 0.90)',
    barB: 'rgba(34, 197, 94, 0.60)',
    emoji: '💨',
  },
  earth: {
    tint: 'rgba(161, 98, 7, 0.20)',         // marrón tierra
    tintStrong: 'rgba(161, 98, 7, 0.32)',
    border: 'rgba(161, 98, 7, 0.24)',
    barA: 'rgba(161, 98, 7, 0.90)',
    barB: 'rgba(161, 98, 7, 0.60)',
    emoji: '🏔️',
  },
  water: {
    tint: 'rgba(56, 189, 248, 0.20)',       // celeste agua
    tintStrong: 'rgba(56, 189, 248, 0.32)',
    border: 'rgba(56, 189, 248, 0.24)',
    barA: 'rgba(56, 189, 248, 0.90)',
    barB: 'rgba(56, 189, 248, 0.60)',
    emoji: '💧',
  },
  neutral: {
    tint: 'rgba(255,255,255,0.12)',
    tintStrong: 'rgba(255,255,255,0.18)',
    border: 'rgba(255,255,255,0.16)',
    barA: 'rgba(218,165,32,0.85)',
    barB: 'rgba(75,0,130,0.85)',
    emoji: '✨',
  },
};

function getElementForSign(signKey) {
  const k = String(signKey || '').toLowerCase().trim();
  return SIGN_ELEMENT[k] || 'neutral';
}

function getThemeForElement(el) {
  const k = String(el || '').toLowerCase().trim();
  return ELEMENT_THEME[k] || ELEMENT_THEME.neutral;
}

const ZC_MAP_CSS = `
  .zc-map-page{
    position: relative;
    -webkit-text-size-adjust: 100%;
    text-rendering: geometricPrecision;

    /* ✅ Make the map reliably scrollable on Android */
    height: 100svh;
    max-height: 100svh;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
    touch-action: pan-y;

    padding-bottom: calc(100px + env(safe-area-inset-bottom));
  }
  .zc-map-page, .zc-map-page *{ box-sizing: border-box; }

  /* Top bar: no recorta texto en iOS/Android + se ve más premium */
  .zc-topbar{
    position: relative;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    margin: 10px 12px 10px;
    padding: calc(10px + var(--zc-safe-top)) 12px 12px;
    border-radius: 18px;
    overflow: hidden;
    background: linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,.07));
    border: 1px solid rgba(255,255,255,.16);
    box-shadow: 0 18px 54px rgba(0,0,0,.30);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    will-change: transform;

    /* ✅ key: allow wrapping on narrow screens so nothing gets clipped */
    flex-wrap: wrap;
  }

  /* Left and right groups must be allowed to shrink */
  .zc-topbar-left,
  .zc-topbar-right{ min-width: 0; }

  .zc-topbar-left{
    display:flex;
    align-items:center;
    gap: 10px;
    min-width: 0;
    flex: 1 1 320px;
  }

  .zc-topbar-right{
    display:flex;
    align-items:center;
    justify-content:flex-end;
    gap: 10px;
    min-width: 0;
    flex: 0 1 320px;
    flex-wrap: wrap;
  }

  /* Prevent the pill/badge from overflowing the topbar */
  .zc-topbar-right > *{ min-width: 0; max-width: 100%; }

  /* Mobile: stack the right group under the title, 100% width */
  @media (max-width: 520px){
    .zc-topbar{
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }
    .zc-topbar-left{ flex: 1 1 auto; width: 100%; }
    .zc-topbar-right{
      flex: 1 1 auto;
      width: 100%;
      justify-content: flex-start;
    }
  }

  /* Grid wrapper: evita cortes en mobile y permite bajar a 1 columna en pantallas ultra angostas */
  .zc-map-grid{
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    /* Prevent the 2px glow/inset layers from peeking out of the right edge on Android */
    padding: 2px;
  }
  @media (max-width: 420px){
    .zc-map-grid{ gap: 10px; }
  }
  @media (max-width: 360px){
    .zc-map-grid{ grid-template-columns: 1fr; }
  }

  .zc-map-wrap{
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
    /* Extra side padding so the card glow/pseudo layers never clip outside the viewport on Android */
    padding-left: max(18px, env(safe-area-inset-left));
    padding-right: max(18px, env(safe-area-inset-right));
  }
  @media (max-width: 420px){
    .zc-map-wrap{ max-width: 520px; }
  }

  /* Card layout helpers (mobile: prevent clipped/overlapping content) */
  .zc-card-top{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
  .zc-sign-col{ min-width: 0; flex: 1 1 auto; }
  .zc-sign-row{ min-width: 0; }
  .zc-lock-col{ flex: 0 0 auto; min-width: 0; max-width: 100%; }

  /* En mobile, el lock/info baja debajo del título para NO tapar el nombre del signo */
  @media (max-width: 520px){
    .zc-card-top{ flex-direction: column; align-items: stretch; gap: 10px; }
    .zc-lock-col{ align-self: stretch; width: 100%; }
    /* Give the unlock hint some breathing room so it doesn't feel glued */
    .zc-lock-col .zc-lock-hint{ margin-top: 2px; }
  }

  /* Keep badges/chips inside card bounds */
  .zc-pill, .zc-world-chip-wrap, .zc-map-content{ max-width: 100%; }
  /* Sign badge: force element emoji always BELOW the sign name, centered */
  .zc-sign-badge{
    max-width: 100%;
    white-space: normal;
    overflow: visible;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
  .zc-sign-main{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    flex-wrap: wrap;
  }
  .zc-sign-emoji{
    margin-top: 4px;
    line-height: 1;
    font-size: 14px;
    opacity: 0.95;
  }
  .zc-sign-text{
    max-width: 100%;
    white-space: nowrap; /* ✅ keep sign names in one line (Capricornio, Sagitario, etc.) */
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    word-break: normal;
    overflow-wrap: normal;
  }

  /* Vivid candy glow layer */
  .zc-map-card{
  position: relative;
  overflow: visible; /* 🔥 CLAVE */
  isolation:isolate;
  min-width: 0;
  margin: 0; /* keep layout tight; grid padding handles glow bleed */
}

/* Solo la capa visual corta, el contenido NO */
.zc-map-visual{
  position: absolute;
  inset: 0;
  border-radius: 18px;
  overflow: hidden;
  pointer-events: none;
}

  /* IMPORTANT: keep content above pseudo-layers */
  .zc-map-content{ position: relative; z-index: 1; max-width: 100%; }

  .zc-map-card::before{
    content:"";
    position:absolute;
    inset:-2px;
    border-radius: 20px;
    background:
      radial-gradient(circle at 16% 14%, rgba(255,255,255,0.26), transparent 46%),
      radial-gradient(circle at 88% 8%, rgba(255,215,0,0.22), transparent 58%),
      radial-gradient(circle at 70% 120%, rgba(108,99,255,0.30), transparent 62%),
      radial-gradient(circle at 20% 120%, rgba(0,229,255,0.18), transparent 55%);
    pointer-events:none;
    opacity: 0.98;
    z-index: 0;
  }

  .zc-map-card::after{
    content:"";
    position:absolute;
    inset:0;
    border-radius: 18px;
    background: linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04) 38%, rgba(0,0,0,0.14));
    mix-blend-mode: screen;
    opacity: 0.72;
    pointer-events:none;
    z-index: 0;
  }

  /* Texture / stars */
  .zc-map-stars{
    position:absolute;
    inset:0;
    pointer-events:none;
    opacity: 0.38;
    mix-blend-mode: screen;
    z-index: 0;
    background-image:
      radial-gradient(circle at 12% 22%, rgba(255,255,255,0.40) 0 1px, transparent 2px),
      radial-gradient(circle at 82% 18%, rgba(255,255,255,0.32) 0 1px, transparent 2px),
      radial-gradient(circle at 44% 78%, rgba(255,255,255,0.34) 0 1px, transparent 2px),
      radial-gradient(circle at 68% 62%, rgba(255,255,255,0.26) 0 1px, transparent 2px),
      radial-gradient(circle at 26% 58%, rgba(255,255,255,0.22) 0 1px, transparent 2px);
  }

  /* Sheen */
  .zc-map-sheen{ position:absolute; inset:0; border-radius: 18px; overflow:hidden; pointer-events:none; z-index: 0; }
  .zc-map-sheen::before{
    content:"";
    position:absolute;
    left:-55%;
    top:-70%;
    width: 80%;
    height: 260%;
    transform: rotate(18deg);
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.26), transparent);
    opacity: 0;
  }
  .zc-map-card:not(.is-locked):hover .zc-map-sheen::before,
  .zc-map-card:not(.is-locked):active .zc-map-sheen::before{
    opacity: 1;
    animation: zcSheen 950ms ease;
  }
  @keyframes zcSheen{
    from{ transform: translateX(-30%) rotate(18deg); }
    to{ transform: translateX(240%) rotate(18deg); }
  }

  /* Progress bar shine */
  .zc-bar{ position: relative; overflow:hidden; }
  .zc-bar-wrap{ transform: translateZ(0); }
  .zc-bar::after{
    content:"";
    position:absolute;
    inset:0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.30), transparent);
    transform: translateX(-80%);
    opacity: 0.85;
    animation: zcBarShine 1.85s ease-in-out infinite;
    pointer-events:none;
  }
  @keyframes zcBarShine{
    0%{ transform: translateX(-80%); }
    55%{ transform: translateX(130%); }
    100%{ transform: translateX(130%); }
  }

  /* Bubble highlight en el glyph */
  .zc-glyph-bubble{ position: relative; overflow:hidden; transform: translateZ(0); }
  .zc-glyph-bubble::after{
    content:"";
    position:absolute;
    inset:0;
    border-radius: 14px;
    background: radial-gradient(circle at 30% 25%, rgba(255,255,255,0.34), transparent 58%);
    pointer-events:none;
  }

  /* Text clarity (evita letras tapadas/recortadas) */
  .zc-map-card *{ -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
  .zc-map-title, .zc-map-subtitle, .zc-map-meta{ line-height: 1.15; }

  /* Pills / chips: padding real para que no se recorten emojis */
  .zc-pill{
    display:inline-flex;
    align-items:center;
    gap:8px;
    padding: 7px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.18);
    background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(0,0,0,0.10));
    box-shadow: 0 12px 24px rgba(0,0,0,0.22);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    text-shadow: 0 2px 12px rgba(0,0,0,0.40);
    white-space: nowrap;
    overflow: visible;
    max-width: 100%;
    min-width: 0;
    flex: 0 1 auto;
  }

  /* Separador candy entre badge y chip */
  .zc-world-sep{
    height: 2px;
    border-radius: 999px;
    margin-top: 6px;
    margin-bottom: 2px;
    opacity: 0.85;
    filter: blur(0.2px);
  }

 /* World chip: must never bleed outside the card on Android */
.zc-world-chip-wrap{
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
}

.zc-world-chip{
  display: inline-flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zc-world-chip > *{ min-width: 0; }
.zc-world-chip-num{ min-width: 0; }

  /* Locked: menos gris apagado, más candy pero claramente bloqueado */
  .zc-map-card.is-locked{
    filter: grayscale(.12) brightness(.88) saturate(.85);
    opacity: 0.92;
  }
  .zc-locked-overlay{
    position:absolute;
    inset:0;
    border-radius: 18px;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.10), rgba(0,0,0,0.26)),
      radial-gradient(circle at 30% 20%, rgba(255,255,255,0.06), transparent 55%);
    pointer-events:none;
    z-index: 0;
  }

  @media (max-width: 420px){
    .zc-topbar{ margin: 10px 10px 10px; padding: calc(10px + var(--zc-safe-top)) 10px 12px; }
    .zc-pill{ padding: 7px 10px; }
    .zc-sign-text{ font-size: 12.5px; }
  }

  @media (max-width: 420px){
  /* más ancho útil y menos “apretado” */
  .zc-map-grid{ gap: 10px; }

  /* la columna del lock no debe flotar ni overlay */
  .zc-lock-col{ align-self: flex-start; }
  .zc-map-card .zc-lock-col *{ max-width: 100%; }

  /* badges un toque más compactos en pantallas chicas */
  .zc-sign-badge{ padding: 6px 10px; }
  .zc-pill{ padding: 7px 10px; }
}

@media (max-width: 360px){
  /* en ultra angosto: 1 columna para evitar cualquier clipping */
  .zc-map-grid{ grid-template-columns: 1fr; }
}

  @media (prefers-reduced-motion: reduce){
    .zc-map-card:not(.is-locked):hover .zc-map-sheen::before,
    .zc-map-card:not(.is-locked):active .zc-map-sheen::before{ animation: none; }
    .zc-bar::after{ animation: none; }
  }
`;

function sumStarsForWorld(levels, starsByLevelObj) {
  let total = 0;
  for (const l of levels) {
    const s = Number(starsByLevelObj?.[String(l.level)] || 0) || 0;
    total += Math.min(3, Math.max(0, s));
  }
  return total;
}

function countCompletedForWorld(levels, starsByLevelObj) {
  let done = 0;
  for (const l of levels) if (Number(starsByLevelObj?.[String(l.level)] || 0) > 0) done += 1;
  return done;
}

function ZodiacCrushMapScreenInner({
  // Navigation
  onBack,
  onClose,

  // Open a world (go to ConstellationMapScreen)
  onOpenWorld,

  // Optional: allow quick-play from the map if you decide to add a CTA later
  onPlayLevel,

  // Current selection (optional)
  selectedLevel,
  selectedLevelFromMap,
  setSelectedLevelFromMap,
}) {
  const { t } = useTranslation('zodiacCrush');
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [levelsErr, setLevelsErr] = useState('');
  const [levels, setLevels] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [openWorld, setOpenWorld] = useState(null); // { worldIndex, signKey }

  const {
    progress,
    loading: progressLoading,
    error: progressError,
    refetch: refetchProgress,
  } = useZodiacCrushMapProgress();

  // Progress is cached in the hook, but keep a safe fallback for first paint / edge cases.
  const safeProgress = useMemo(
    () => (progress && typeof progress === 'object'
      ? progress
      : { maxUnlockedLevel: 1, starsByLevel: {} }),
    [progress]
  );

  const loading = !!levelsLoading || !!progressLoading;
  const err = levelsErr || progressError || '';

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLevelsLoading(true);
        setLevelsErr('');

        const lvlRes = await zcMapGet('/api/games/zodiac-crush/map/levels?from=1&to=240');

        if (!alive) return;
        setLevels(Array.isArray(lvlRes.levels) ? lvlRes.levels : []);
      } catch (e) {
        if (!alive) return;
        setLevelsErr(e?.message || t('mapLoadError'));
      } finally {
        if (!alive) return;
        setLevelsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [t]);
  // Local QA convenience only. The shared helper is hard-disabled in production.
  const qaUnlockAll = useMemo(() => isZodiacCrushQaUnlockEnabled(), []);

  const worlds = useMemo(() => {
    if (!Array.isArray(levels) || !levels.length || !safeProgress) return [];

    const starsByLevel = safeProgress?.starsByLevel || {};

    const byWorld = new Map();
    for (const l of levels) {
      const wi = Number(l.worldIndex);
      if (!byWorld.has(wi)) byWorld.set(wi, []);
      byWorld.get(wi).push(l);
    }

    const result = [];
    for (let wi = 0; wi < 12; wi += 1) {
      const worldLevels = (byWorld.get(wi) || []).slice().sort((a, b) => (a.indexInWorld || 0) - (b.indexInWorld || 0));
      const completed = countCompletedForWorld(worldLevels, starsByLevel);
      const starsTotal = sumStarsForWorld(worldLevels, starsByLevel);

      // World unlock rule: worldIndex 0 => level 1..20, worldIndex 1 => 21..40, etc.
      const worldStartLevel = (wi * 20) + 1;
      const locked = !qaUnlockAll && Number(worldStartLevel) > Number(safeProgress.maxUnlockedLevel || 1);

      const meta = WORLD_LABELS_ES[wi] || { key: `world_${wi}`, label: t('worldNumber', { number: wi + 1 }), glyph: '✦' };
      const element = getElementForSign(meta.key);
      result.push({
  worldIndex: wi,
  signKey: meta.key,
  label: t(`signs.${meta.key}`, { defaultValue: meta.label }),
  glyph: meta.glyph,
  element,
  levels: worldLevels,
  completed,
  starsTotal,
  locked,
});
    }

    return result;
  }, [levels, safeProgress, qaUnlockAll, t]);

  const handleOpenWorld = (worldIndex, signKey) => {
    // If parent provided navigation, use it.
    if (typeof onOpenWorld === 'function') {
      onOpenWorld({ worldIndex, signKey });
      return;
    }

    // Otherwise, open the world inside this screen.
    setOpenWorld({
      worldIndex: Number(worldIndex) || 0,
      signKey: String(signKey || 'aries'),
    });
  };

  const styles = useMemo(
    () => ({
      page: {
        minHeight: '100vh',
        height: '100svh',
        overflowX: 'hidden',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain',
        touchAction: 'pan-y',
        paddingTop: 'calc(18px + env(safe-area-inset-top))',
        
        paddingBottom: 'calc(110px + env(safe-area-inset-bottom))',
        color: 'rgba(255,255,255,0.92)',
        background:
          'radial-gradient(circle at 16% 2%, rgba(255,59,59,0.20), transparent 52%),' +
          'radial-gradient(circle at 84% 6%, rgba(56,189,248,0.22), transparent 56%),' +
          'radial-gradient(circle at 12% 92%, rgba(34,197,94,0.16), transparent 60%),' +
          'radial-gradient(circle at 86% 112%, rgba(161,98,7,0.16), transparent 62%),' +
          'radial-gradient(circle at 52% 40%, rgba(255,255,255,0.07), transparent 55%),' +
          'radial-gradient(circle at 55% 0%, rgba(218,165,32,0.26), transparent 55%),' +
          'radial-gradient(circle at 75% 112%, rgba(75,0,130,0.42), transparent 62%),' +
          'linear-gradient(180deg, rgba(7,5,12,1), rgba(10,8,18,1))',
      },
      topbar: {
        display: 'flex',
        padding: '10px 10px',
        borderRadius: 18,
        background:
          'radial-gradient(circle at 18% 0%, rgba(255,59,59,0.18), transparent 60%),' +
          'radial-gradient(circle at 82% 0%, rgba(56,189,248,0.18), transparent 60%),' +
          'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.07))',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 18px 48px rgba(0,0,0,0.30)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 12,
      },
      topbarLeft: { display: 'flex', alignItems: 'center', gap: 10 },
      topbarBtns: { display: 'flex', alignItems: 'center', gap: 8 },
      btn: {
        padding: '9px 12px',
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.16)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(0,0,0,0.10))',
        transition: 'transform 120ms ease, filter 120ms ease',
        color: 'rgba(255,255,255,0.92)',
        fontWeight: 900,
        cursor: 'pointer',
        boxShadow: '0 12px 22px rgba(0,0,0,0.28)',
        whiteSpace: 'nowrap',
      },
      btnIcon: {
        padding: '9px 10px',
        minWidth: 42,
        textAlign: 'center',
      },
      titleWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
      title: {
        fontWeight: 950,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        margin: 0,
        fontSize: 18,
        filter:
          'drop-shadow(0 2px 12px rgba(75,0,130,0.35)) drop-shadow(0 2px 12px rgba(218,165,32,0.18))',
      },
      subtitle: { margin: 0, opacity: 0.82, fontWeight: 700, fontSize: 12.5 },
      pill: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '8px 11px',
        borderRadius: 999,
        fontWeight: 950,
        fontSize: 12,
        background: 'linear-gradient(135deg, rgba(255,215,0,0.22), rgba(75,0,130,0.22))',
border: '1px solid rgba(255,215,0,0.22)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
        whiteSpace: 'nowrap',
      },
      badge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '7px 10px',
        borderRadius: 999,
        fontWeight: 950,
        fontSize: 12,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.22)',
        whiteSpace: 'nowrap',
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 12,
        alignItems: 'stretch',
      },
      card: {
        position: 'relative',
        borderRadius: 18,
        padding: '14px 12px 12px',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.07))',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 18px 52px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.10)',
        overflow: 'visible',
        cursor: 'pointer',
        userSelect: 'none',
        transform: 'translateY(0)',
        transition: 'transform 140ms ease, filter 140ms ease, box-shadow 140ms ease',
      },
      cardLocked: {
        cursor: 'not-allowed',
        filter: 'grayscale(0.45) saturate(0.6) brightness(0.78)',
        opacity: 0.85,
      },
      cardGlow: {
        content: '""',
      },
      cardTop: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        minWidth: 0,
      },
      signRow: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
      glyph: {
        width: 36,
        height: 36,
        borderRadius: 14,
        display: 'grid',
        placeItems: 'center',
        fontSize: 18,
        fontWeight: 900,
        background: 'linear-gradient(135deg, rgba(255,215,0,0.26), rgba(75,0,130,0.32))',
        border: '1px solid rgba(255,215,0,0.18)',
        boxShadow: '0 16px 34px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.16)',
      },
      signName: {
        fontWeight: 950,
        letterSpacing: 0.2,
        lineHeight: 1.12,
        paddingBottom: 1,
        textShadow:
          '0 14px 34px rgba(0,0,0,0.34), 0 0 24px rgba(255,255,255,0.10)',
      },
      signBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 999,
        background:
          'radial-gradient(circle at 18% 15%, rgba(255,255,255,0.22), rgba(255,255,255,0.08) 55%, rgba(0,0,0,0.10))',
        border: '1px solid rgba(255,255,255,0.16)',
        boxShadow:
          '0 14px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.10)',
        flexWrap: 'wrap',
        rowGap: 4,
        whiteSpace: 'normal',
        lineHeight: 1.1,
        
        overflow: 'visible',
        textOverflow: 'clip',
        transform: 'translateZ(0)',
        maxWidth: '100%',
        minWidth: 0,
      },
      signBadgeDot: {
        width: 10,
        height: 10,
        borderRadius: 999,
        boxShadow: '0 10px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.22)',
        flex: '0 0 auto',
      },
      signBadgeText: {
        fontWeight: 950,
        letterSpacing: 0.15,
        lineHeight: 1,
        fontSize: 'clamp(11.6px, 3.25vw, 16px)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        maxWidth: '100%',
      },
      lock: { fontSize: 18,
  opacity: 0.95,
  marginTop: 6,
  flexShrink: 0, },
      lockInfo: {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  minWidth: 0,
  maxWidth: '100%',
},
      lockHint: {
  fontSize: 11,
  fontWeight: 950,
  letterSpacing: 0.2,
  opacity: 0.94,
  textAlign: 'left',
  textShadow: '0 10px 26px rgba(0,0,0,0.28)',
  padding: '6px 8px',
  borderRadius: 12,
  background: 'linear-gradient(180deg, rgba(0,0,0,0.22), rgba(0,0,0,0.10))',
  border: '1px solid rgba(255,255,255,0.14)',
  boxShadow: '0 12px 22px rgba(0,0,0,0.22)',
  width: '100%',
  maxWidth: '100%',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  lineHeight: 1.15,
  marginTop: 2,
  marginBottom: 4,
},
      lockMiniBarWrap: {
        width: '100%',
        height: 8,
        borderRadius: 999,
        background: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.12)',
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
      },
      lockMiniBar: {
        height: '100%',
        borderRadius: 999,
        width: '0%',
        background: 'linear-gradient(90deg, rgba(255,255,255,0.30), rgba(255,255,255,0.06))',
      },
      lockMiniPct: {
        fontSize: 10.5,
        fontWeight: 900,
        opacity: 0.78,
        marginTop: -2,
      },
      metaRow: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  opacity: 0.92,
  fontWeight: 800,
  fontSize: 12,
  lineHeight: 1.15,
},
      small: { opacity: 0.80, fontWeight: 850, lineHeight: 1.15 },
      worldChipRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 10,
        flexWrap: 'wrap',
        maxWidth: '100%',
        minWidth: 0,
      },
      worldChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '6px 10px',
        borderRadius: 999,
        fontWeight: 950,
        fontSize: 'clamp(10.8px, 3.1vw, 11.5px)',
        letterSpacing: 0.2,
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow:
          '0 12px 24px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.10)',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      worldChipDot: {
        width: 9,
        height: 9,
        borderRadius: 999,
        boxShadow: '0 10px 18px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.22)',
        flex: '0 0 auto',
      },
      worldChipDivider: {
        height: 10,
        width: 1,
        borderRadius: 999,
        background: 'rgba(255,255,255,0.22)',
        opacity: 0.9,
        flex: '0 0 auto',
      },
      worldChipSubtle: {
        opacity: 0.92,
        fontWeight: 950,
        lineHeight: 1,
        paddingLeft: 2,
        paddingRight: 2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translateY(0.5px)',
        textShadow: '0 10px 22px rgba(0,0,0,0.28)',
        minWidth: 0,
        whiteSpace: 'nowrap',
      },
      barWrap: {
  marginTop: 10,
  height: 12,                 // un poquito más alta: evita clipping en mobile
  borderRadius: 999,
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.14)',
  overflow: 'hidden',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)',
  transform: 'translateZ(0)', // fuerza capa: evita cortes raros iOS
},
bar: {
  height: '100%',
  borderRadius: 999,
  width: '0%',
  background: 'linear-gradient(90deg, rgba(218,165,32,0.85), rgba(75,0,130,0.85))',
  willChange: 'width',
  transform: 'translateZ(0)',
},
      errorBox: {
        marginTop: 12,
        padding: '12px 12px',
        borderRadius: 16,
        background: 'rgba(255,80,80,0.10)',
        border: '1px solid rgba(255,80,80,0.18)',
        color: 'rgba(255,255,255,0.92)',
        fontWeight: 800,
      },
      hintBox: {
        marginTop: 10,
        padding: '10px 12px',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: 'rgba(255,255,255,0.82)',
        fontWeight: 750,
        fontSize: 12.5,
      },
      loader: {
        marginTop: 12,
        padding: '12px 12px',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        fontWeight: 850,
      },
    }),
    []
  );

  const totalStars = useMemo(() => {
    const starsObj = safeProgress?.starsByLevel || {};
    return Object.values(starsObj).reduce((acc, v) => {
      const s = Number(v || 0) || 0;
      return acc + Math.min(3, Math.max(0, s));
    }, 0);
  }, [safeProgress]);

  const totalCompleted = useMemo(() => {
    const starsObj = safeProgress?.starsByLevel || {};
    return Object.values(starsObj).reduce((acc, v) => acc + (Number(v) > 0 ? 1 : 0), 0);
  }, [safeProgress]);

  if (openWorld) {
    // allow optional deep-linking /zodiac-crush?level=XX while staying in the world view
    let selectedLevelFromUrl = null;
    try {
      const sp = new URLSearchParams(location.search || '');
      const n = parseInt(sp.get('level') || '', 10);
      if (Number.isFinite(n) && n > 0) selectedLevelFromUrl = n;
    } catch (_) {}

    return (
      <ConstellationMapScreen
        worldIndex={openWorld.worldIndex}
        signKey={openWorld.signKey}
        selectedLevel={selectedLevelFromUrl || selectedLevel || selectedLevelFromMap}
        onBack={() => {
  setOpenWorld(null);
  try { refetchProgress(); } catch (_) {}
}}

onClose={() => {
  setOpenWorld(null);
  try { refetchProgress(); } catch (_) {}
}}
        onPlayLevel={({ level }) => {
  const lvl = Number(level) || 1;

  // ✅ Si el padre te pasó handler, usalo (no cambies de ruta)
  if (typeof onPlayLevel === 'function') {
    onPlayLevel({ level: lvl });
    return;
  }

  // fallback: deep link si se usa standalone
  navigate(`/zodiac-crush/play?level=${encodeURIComponent(String(lvl))}`);
}}
      />
    );
  }

 return (
  <div style={styles.page} className="zc-map-page">
    <style>{ZC_MAP_CSS}</style>
    <div className="zc-map-wrap">
    <div style={styles.topbar} className="zc-topbar">
        <div style={styles.topbarLeft} className="zc-topbar-left">
          <div style={styles.topbarBtns}>
            <button
              style={{ ...styles.btn, ...styles.btnIcon }}
              onClick={() => {
                if (typeof onBack === 'function') onBack();
                else navigate('/home');
              }}
              type="button"
              aria-label={t('back')}
              title={t('back')}
            >
              ←
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnIcon }}
              onClick={() => {
                if (typeof onClose === 'function') onClose();
                else if (typeof onBack === 'function') onBack();
                else navigate('/home');
              }}
              type="button"
              aria-label={t('close')}
              title={t('close')}
            >
              ✕
            </button>
          </div>

          <div style={styles.titleWrap}>
            <h2 style={styles.title}>Zodiac Crush</h2>
            <p style={styles.subtitle}><ZodiacCrushMapSummary worldCount={12} levelCount={240} /></p>
          </div>
        </div>

        <div className="zc-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {Number.isFinite(Number(selectedLevel || selectedLevelFromMap)) ? (
            <div style={styles.badge} title={t('selectedLevel')}>
              <span style={{ opacity: 0.9 }}>🎮</span>
              <span>{t('level')} {Number(selectedLevel || selectedLevelFromMap)}</span>
            </div>
          ) : null}

          <div style={styles.pill} title={t('totalProgress')}>
            <span>⭐ {totalStars}</span>
            <span style={{ opacity: 0.85 }}>·</span>
            <span>{totalCompleted}/240</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={styles.loader}>{t('loadingMap')}</div>
      ) : err ? (
        <div style={styles.errorBox}>
          <div>⚠️ {err}</div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{
                ...styles.btn,
                padding: '8px 10px',
                borderRadius: 12,
                fontSize: 12,
              }}
              onClick={() => {
                // Reload both data sources
                setLevelsLoading(true);
                setLevelsErr('');
                // Trigger levels reload by re-running the effect pattern manually
                (async () => {
                  try {
                    const lvlRes = await zcMapGet('/api/games/zodiac-crush/map/levels?from=1&to=240');
                    setLevels(Array.isArray(lvlRes.levels) ? lvlRes.levels : []);
                  } catch (e) {
                    setLevelsErr(e?.message || t('mapLoadError'));
                  } finally {
                    setLevelsLoading(false);
                  }
                })();

                // Refetch progress from backend (hook)
                try { refetchProgress(); } catch (_) {}
              }}
            >
              ↻ {t('retry')}
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.grid} className="zc-map-grid">
          {worlds.map((w) => {
            const pctRaw = w.levels.length ? (w.completed / w.levels.length) * 100 : 0;
            const pct = Math.max(0, Math.min(100, Math.round(pctRaw || 0)));
            const theme = getThemeForElement(w.element);
const cardBg =
  `radial-gradient(circle at 18% 15%, ${theme.tintStrong}, transparent 58%),` +
  `radial-gradient(circle at 82% 110%, ${theme.tint}, transparent 62%),` +
  'linear-gradient(180deg, rgba(255,255,255,0.20), rgba(255,255,255,0.08))';
            return (
              <div
                key={w.worldIndex}
                className={`zc-map-card${w.locked ? ' is-locked' : ''}`}
                style={{
  ...styles.card,
  background: cardBg,
  border: `1px solid ${theme.border}`,
  ...(w.locked ? styles.cardLocked : null),
}}
                aria-label={
                  w.locked
                    ? t('lockedWorldLabel', { number: w.worldIndex + 1, label: w.label })
                    : t('openWorldLabel', { number: w.worldIndex + 1, label: w.label, percent: pct, stars: w.starsTotal })
                }
                title={
                  w.locked
                    ? t('lockedWorldTitle', { level: (w.worldIndex * 20) + 1 })
                    : t('openWorldTitle', { completed: w.completed, total: w.levels.length || 20, stars: w.starsTotal, percent: pct })
                }
                onClick={() => {
                  if (w.locked) return;
                  handleOpenWorld(w.worldIndex, w.signKey);
                }}
                onMouseEnter={(e) => {
  if (w.locked) return;
  e.currentTarget.style.transform = 'translateY(-2px)';
  e.currentTarget.style.filter = 'brightness(1.06) saturate(1.05)';
  e.currentTarget.style.boxShadow = '0 22px 64px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)';
}}
                onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'translateY(0px)';
  e.currentTarget.style.filter = 'none';
  e.currentTarget.style.boxShadow = styles.card.boxShadow;
}}
                onKeyDown={(e) => {
                  if (w.locked) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenWorld(w.worldIndex, w.signKey);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="zc-map-visual">
  <div className="zc-map-stars" />
  <div className="zc-map-sheen" />
  {w.locked ? <div className="zc-locked-overlay" /> : null}
</div>

                <div className="zc-map-content">
                  <div style={styles.cardTop} className="zc-card-top">
                    <div style={styles.signRow} className="zc-sign-row">
                      <div style={styles.glyph} className="zc-glyph-bubble">{w.glyph}</div>
                      <div className="zc-sign-col">
                        <div style={styles.signName}>
                          <span
                            className="zc-sign-badge"
                            style={{
                              ...styles.signBadge,
                              background:
                                `radial-gradient(circle at 20% 10%, rgba(255,255,255,0.26), rgba(255,255,255,0.10) 55%, rgba(0,0,0,0.10)), radial-gradient(circle at 85% 120%, ${theme.tintStrong}, transparent 62%)`,
                              border: `1px solid ${theme.border}`,
                            }}
                          >
                            <span className="zc-sign-main">
                              <span
                                aria-hidden="true"
                                style={{
                                  ...styles.signBadgeDot,
                                  background: `linear-gradient(180deg, ${theme.barA}, ${theme.barB})`,
                                }}
                              />
                              <span style={styles.signBadgeText} className="zc-sign-text">{w.label}</span>
                            </span>
                            <span className="zc-sign-emoji" aria-hidden="true">{theme.emoji}</span>
                          </span>
                        </div>
                        <div
                          className="zc-world-sep"
                          aria-hidden="true"
                          style={{
                            background: `linear-gradient(90deg, rgba(255,255,255,0.00), ${theme.barA}, ${theme.barB}, rgba(255,255,255,0.00))`,
                          }}
                        />

                        <div style={styles.worldChipRow}>
                          <div className="zc-world-chip-wrap">
                            <span
                              className="zc-world-chip"
                              style={{
                                ...styles.worldChip,
                                border: `1px solid ${theme.border}`,
                                background:
                                  `linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06)), radial-gradient(circle at 90% 120%, ${theme.tintStrong}, transparent 62%)`,
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  ...styles.worldChipDot,
                                  background: `linear-gradient(180deg, ${theme.barA}, ${theme.barB})`,
                                }}
                              />
                              <span>{t('world')}</span>
                              <span style={styles.worldChipDivider} aria-hidden="true" />
                              <span className="zc-world-chip-num" style={styles.worldChipSubtle}>{w.worldIndex + 1}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                  {(() => {
                    const unlockAt = (w.worldIndex * 20) + 1;

                    if (w.locked) {
                      return (
                        <div className="zc-lock-col">
                          <div style={styles.lockInfo}>
                            <div
                              style={styles.lockHint}
                              className="zc-lock-hint"
                              aria-label={t('unlockAtLevel', { level: unlockAt })}
                            >
                              {t('unlockAtLevel', { level: unlockAt })}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="zc-lock-col">
                        <div style={styles.lock} aria-label={t('openWorld')} title={t('openWorld')}>
                          🔑
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={styles.metaRow} className="zc-map-meta">
                  <span>
                    {w.completed}/{w.levels.length || 20} {t('levels')}
                  </span>
                  <span>⭐ {w.starsTotal}</span>
                  <span style={{ opacity: 0.86, fontWeight: 900 }}>{pct}%</span>
                </div>

                  <div
  style={styles.barWrap}
  className="zc-bar-wrap"
  aria-label={t('worldProgress', { percent: pct })}
>
  <div
    className="zc-bar"
    style={{
      ...styles.bar,
      width: `${pct}%`,
      background: `linear-gradient(90deg, ${theme.barA}, ${theme.barB})`,
    }}
  />
</div>
</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={styles.hintBox}>
        {err
          ? t('mapIncomplete')
          : loading
            ? t('loadingStarMap')
            : t('mapHint')}
        <span style={{ display: 'inline-block', marginLeft: 6, opacity: 0.9 }}>✨</span>
      </div>
      <div aria-hidden="true" style={{ height: 14 }} />
    </div>
    </div>
  );
}

// Wrapper: force remount when returning to the map so cached hook state/UI never shows stale progress
export default function ZodiacCrushMapScreen(props) {
  const location = useLocation();
  const routeKey = `${location.pathname}|${location.search}|${location.key}`;
  return <ZodiacCrushMapScreenInner key={routeKey} {...props} />;
}
